// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Star Systems Data - Universe Dungeon Dimensions
 * 
 * The spaces between spaces where mathematics breaks down and Things live.
 * A realm of eldritch horror and impossible geometry from Terry Pratchett's Discworld.
 * Where the number 8 is considered unlucky and reality is more of a suggestion.
 */

export const starSystems = [
  // Starting area - Arrival point from Discworld
  { 
    id: 100, 
    name: 'REALITY ANCHOR', 
    type: 'station',
    distance: 0, // Origin point in this nightmare
    threat: 'EXISTENTIAL', 
    resources: 'SANITY DRAIN',
    services: ['refuel', 'repairs'],
    tradeType: 'default',
    primaryColor: '#4B0082',
    secondaryColor: '#8B008B',
    hasRings: true,
    description: 'Emergency anchor point maintaining a bubble of reality. Last safe harbor before the madness begins.'
  },
  { 
    id: 101, 
    name: 'THE SCREAMING VOID', 
    type: 'planet',
    distance: 15, 
    threat: 'ELDRITCH', 
    resources: 'PURE CHAOS',
    services: ['refuel', 'exploration'],
    tradeType: 'cursed',
    primaryColor: '#2F2F2F',
    secondaryColor: '#4B0082',
    hasRings: false,
    description: 'A place where sound has color and mathematics weeps. The screaming never stops.'
  },
  { 
    id: 102, 
    name: 'IMPOSSIBLE ANGLES', 
    type: 'planet',
    distance: 25, 
    threat: 'GEOMETRIC', 
    resources: 'BROKEN MATH',
    services: ['refuel', 'exploration'],
    tradeType: 'paradox',
    primaryColor: '#8B008B',
    secondaryColor: '#4B0082',
    hasRings: true,
    description: 'Where triangles have four sides and circles are square. Geometry professors go mad here.'
  },
  { 
    id: 103, 
    name: 'THINGS GATHERING PLACE', 
    type: 'station',
    distance: 40, 
    threat: 'UNSPEAKABLE', 
    resources: 'NIGHTMARE FUEL',
    services: ['refuel', 'weapons'],
    tradeType: 'horror',
    primaryColor: '#2F2F2F',
    secondaryColor: '#8B0000',
    hasRings: false,
    description: 'Where the Things from the Dungeon Dimensions congregate. They notice you noticing them.'
  },
  { 
    id: 104, 
    name: 'MATHEMATICS BREAKDOWN ZONE', 
    type: 'planet',
    distance: 50, 
    threat: 'LOGICAL', 
    resources: 'PARADOX ENERGY',
    services: ['refuel', 'exploration'],
    tradeType: 'impossible',
    primaryColor: '#4B0082',
    secondaryColor: '#8B008B',
    hasRings: true,
    description: 'Where 2+2=purple and infinity is a small number. Calculators spontaneously combust.'
  },
  { 
    id: 105, 
    name: 'THE COLOUR OF MAGIC', 
    type: 'planet',
    distance: 60, 
    threat: 'OCTARINE', 
    resources: 'RAW MAGIC',
    services: ['refuel', 'exploration'],
    tradeType: 'magical',
    primaryColor: '#FF00FF',
    secondaryColor: '#00FF00',
    hasRings: false,
    description: 'The eighth color of the spectrum. Visible only to wizards and cats. Highly unstable.'
  },
  { 
    id: 106, 
    name: 'PROBABILITY STORM', 
    type: 'planet',
    distance: 80, 
    threat: 'IMPROBABLE', 
    resources: 'CHANCE ITSELF',
    services: ['refuel', 'exploration'],
    tradeType: 'random',
    primaryColor: '#8B008B',
    secondaryColor: '#4B0082',
    hasRings: true,
    description: 'Where unlikely events become certainties. Your ship might turn into a bowl of petunias.'
  },
  { 
    id: 107, 
    name: 'THE EIGHT FOLD PATH', 
    type: 'planet',
    distance: 95, 
    threat: 'UNLUCKY', 
    resources: 'MISFORTUNE',
    services: ['refuel', 'exploration'],
    tradeType: 'cursed',
    primaryColor: '#2F2F2F',
    secondaryColor: '#8B0000',
    hasRings: false,
    description: 'The number that shall not be named. Eight paths leading to eight kinds of doom.'
  },
  { 
    id: 108, 
    name: 'REALITY FRACTURE POINT', 
    type: 'station',
    distance: 120, 
    threat: 'DIMENSIONAL', 
    resources: 'BROKEN SPACE',
    services: ['refuel', 'weapons'],
    tradeType: 'horror',
    primaryColor: '#4B0082',
    secondaryColor: '#8B008B',
    hasRings: false,
    description: 'Where reality cracked and never healed. Things seep through the gaps.'
  },
  { 
    id: 109, 
    name: 'THE LUGGAGE GRAVEYARD', 
    type: 'planet',
    distance: 140, 
    threat: 'SAPIENT PEARWOOD', 
    resources: 'ABANDONED LUGGAGE',
    services: ['refuel', 'exploration'],
    tradeType: 'cursed',
    primaryColor: '#8B4513',
    secondaryColor: '#2F2F2F',
    hasRings: true,
    description: 'Where lost luggage goes to die. Hundreds of little legs scuttle in the darkness.'
  },
  { 
    id: 110, 
    name: 'THE UNSPACE', 
    type: 'planet',
    distance: 160, 
    threat: 'NONEXISTENT', 
    resources: 'NOTHING',
    services: ['refuel', 'exploration'],
    tradeType: 'void',
    primaryColor: '#000000',
    secondaryColor: '#2F2F2F',
    hasRings: false,
    description: 'A place that isn\'t there. You can visit it, but it doesn\'t exist. Paradox incarnate.'
  },
  { 
    id: 111, 
    name: 'DUNGEON DIMENSIONS NEXUS', 
    type: 'station',
    distance: 180, 
    threat: 'ULTIMATE HORROR', 
    resources: 'PURE TERROR',
    services: ['refuel', 'weapons', 'upgrades'],
    tradeType: 'nightmare',
    primaryColor: '#8B0000',
    secondaryColor: '#4B0082',
    hasRings: false,
    description: 'The heart of the Dungeon Dimensions. Where Things plot the unmaking of reality.'
  },
  { 
    id: 112, 
    name: 'THE LAST THEOREM', 
    type: 'planet',
    distance: 200, 
    threat: 'MATHEMATICAL', 
    resources: 'PROOF BY CONTRADICTION',
    services: ['refuel', 'exploration'],
    tradeType: 'paradox',
    primaryColor: '#4B0082',
    secondaryColor: '#8B008B',
    hasRings: true,
    description: 'The final mathematical proof that proves mathematics is impossible. QED.'
  },
  { 
    id: 113, 
    name: 'THE THING THAT SHOULD NOT BE', 
    type: 'planet',
    distance: 250, 
    threat: 'UNSPEAKABLE', 
    resources: 'EXISTENTIAL DREAD',
    services: ['refuel', 'exploration'],
    tradeType: 'horror',
    primaryColor: '#2F2F2F',
    secondaryColor: '#8B0000',
    hasRings: false,
    description: 'It exists because it shouldn\'t. It shouldn\'t because it does. Do not think about it.'
  },
  { 
    id: 114, 
    name: 'THE EDGE OF REASON', 
    type: 'station',
    distance: 300, 
    threat: 'MADNESS', 
    resources: 'LOST SANITY',
    services: ['refuel', 'weapons', 'upgrades'],
    tradeType: 'nightmare',
    primaryColor: '#8B008B',
    secondaryColor: '#4B0082',
    hasRings: false,
    description: 'The last outpost before complete madness. Beyond here, even the Things fear to tread.'
  }
];

// Star system positions on the map (3D coordinates for visual layout)
export const starPositions = {
  100: { x: 30, y: 50, z: 0 },   // NEXUS GATE - starting point
  101: { x: 40, y: 45, z: 5 },   // CRYSTALLINE NEBULA
  102: { x: 35, y: 55, z: -5 },  // QUANTUM STAR
  103: { x: 50, y: 50, z: 0 },   // VOID STATION PRIME
  104: { x: 55, y: 40, z: 10 },  // PLASMA VORTEX
  105: { x: 45, y: 60, z: -8 },  // ECHO BINARY
  106: { x: 60, y: 35, z: 12 },  // SINGULARITY FORGE
  107: { x: 40, y: 65, z: -10 }, // HARMONIC RESONANCE
  108: { x: 65, y: 50, z: 8 },   // DIMENSIONAL RIFT STATION
  109: { x: 55, y: 55, z: -6 },  // PHASE SHIFT CLUSTER
  110: { x: 70, y: 45, z: 15 },  // ANTIMATTER CORE
  111: { x: 60, y: 60, z: -12 }, // NEXUS TRADING HUB
  112: { x: 75, y: 40, z: 18 },  // TEMPORAL ANOMALY
  113: { x: 80, y: 50, z: 20 },  // VOID HEART
  114: { x: 85, y: 55, z: 22 },  // FRONTIER OUTPOST NEXUS
};
